import { FormikErrors, FormikHelpers, useFormik } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { PopoverInput } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'

import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'

import { Box } from '@shared/components/Box'

interface AddToGroupModalProps {
  onSubmit: (values: Partial<AddToGroupFormValues>) => Promise<void>
  initialValues?: Partial<AddToGroupFormValues>
  groups: IntroducingBrokerRebateGroupDto[]
}

export const AddToGroupModal: BaseModalType<AddToGroupModalProps> = (props) => {
  const { onSubmit, initialValues, close, groups } = props

  const { t } = useTranslation()

  const { setFieldValue, errors, values, handleSubmit, setErrors, isSubmitting } =
    useAddToGroupForm({
      initialValues: initialValues || DEFAULT_VALUES,
      handleSubmit: async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true)
        await onSubmit(values)
        setSubmitting(false)
        resetForm()
      },
    })

  const hasErrors = useMemo(() => Object.values(errors).some((error) => !!error), [errors])

  const isApplyDisabled = hasErrors || isSubmitting || !values.groupId

  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === values.groupId),
    [groups, values.groupId]
  )

  const groupOptions = useMemo(
    () => groups.map((group) => ({ id: group.id, name: group.name || group.id })),
    [groups]
  )

  const handleChange = async (key: string, value: any) => {
    setFieldValue(key, value)

    if (hasErrors) {
      setErrors({
        ...errors,
        [key]: undefined,
      })
    }
  }

  return (
    <Modal>
      <Modal.Header>{t('IB.RebatesPage.ClientsPage.Adding to group')}</Modal.Header>
      <Modal.Body>
        <PopoverInput
          value={values.groupId}
          onChange={(option) => handleChange('groupId', option.id)}
          options={groupOptions}
          label={t('IB.RebatesPage.ClientsPage.Available groups')}
          hint={errors.groupId}
          error={!!errors.groupId}
          isFullWidth
        />
        {selectedGroup && (
          <>
            <Box flex direction='column' gap='x8' fullWidth>
              <Box flex align='center' gap='x8' justify='space-between' fullWidth>
                <Modal.Body.Text>{t('Type')}</Modal.Body.Text>
                <Modal.Body.Text color='text-high-em' medium>
                  {selectedGroup?.rebateApprovalType.name}
                </Modal.Body.Text>
              </Box>
              <Box flex align='center' gap='x8' justify='space-between' fullWidth>
                <Modal.Body.Text>{t('IB.RebatesPage.Payout period')}</Modal.Body.Text>
                <Modal.Body.Text color='text-high-em' medium>
                  {selectedGroup?.rebatePeriod.name}
                </Modal.Body.Text>
              </Box>
              <Box flex align='center' gap='x8' justify='space-between' fullWidth>
                <Modal.Body.Text>{t('IB.RebatesPage.ClientsPage.Commission')}</Modal.Body.Text>
                <Modal.Body.Text color='text-high-em' medium>
                  {selectedGroup?.rebateAmountInPercentage}%
                </Modal.Body.Text>
              </Box>
            </Box>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary disabled={isApplyDisabled} onClick={() => handleSubmit()}>
          {t('Apply')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

export interface AddToGroupFormValues {
  groupId: string
}

interface AddToGroupFormParams {
  handleSubmit: (
    values: Partial<AddToGroupFormValues>,
    formikHelpers: FormikHelpers<Partial<AddToGroupFormValues>>
  ) => void
  initialValues?: Partial<AddToGroupFormValues>
}

const DEFAULT_VALUES: Partial<AddToGroupFormValues> = {
  groupId: undefined,
}

export const useAddToGroupForm = ({
  handleSubmit,
  initialValues = DEFAULT_VALUES,
}: AddToGroupFormParams): ReturnType<typeof useFormik<Partial<AddToGroupFormValues>>> => {
  const { t } = useTranslation()

  return useFormik<Partial<AddToGroupFormValues>>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validate: (values) => {
      const errors: FormikErrors<AddToGroupFormValues> = {}

      if (!values.groupId) {
        errors.groupId = t('Validation.Required')
      }

      return errors
    },
    validateOnChange: false,
  })
}
